import { Input } from "@/components/ui/input";
import { useUserStore } from "@/store/userStore";
import { Briefcase, LinkIcon, BriefcaseBusiness, Edit2 } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

interface EnterpriseCompanyInfoProps {
  formData: {
    companyName: string;
    industryType: string;
    socialMediaProfile: string;
  };
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  updateEnterpriseProfile: (
    id: string,
    data: Partial<any>,
    token: string
  ) => Promise<void>;
  handleCancel?: () => void;
  isClientProfile?:boolean
}

export const EnterpriseCompanyInfo = ({
  isClientProfile,
  formData,
  handleInputChange,
  updateEnterpriseProfile,
  handleCancel
}: EnterpriseCompanyInfoProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  const { user } = useUserStore();

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!user?.accessToken || !user?.userId) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile.",
        variant: "destructive",
      });
      return;
    }

    const updatedData = {
      ...formData,
      companyName: formData.companyName,
    };

    try {
      await updateEnterpriseProfile(
        user?.userId,
        updatedData,
        user?.accessToken
      );
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your enterprise profile has been successfully updated.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="hover:shadow-md transition-all duration-300 border-blue-100">
      <CardHeader className="pb-2">
        <div className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            Company Information
          </CardTitle>
          {!isEditing && !isClientProfile && (
            <Button variant="ghost" size="sm" onClick={handleEditClick}>
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <BriefcaseBusiness className="w-4 h-4 text-gray-500" />
                <Input
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Company name"
                  required
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="font-semibold">Company Name:</span>
                <span className="text-gray-600">{formData.companyName}</span>
              </div>
            )}

            {isEditing ? (
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gray-500" />
                <Input
                  name="industryType"
                  value={formData.industryType}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Industry type"
                />
              </div>
            ) : (
              formData.industryType && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Industry Type:</span>
                  <span className="text-gray-600">{formData.industryType}</span>
                </div>
              )
            )}

            {isEditing ? (
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-gray-500" />
                <Input
                  name="socialMediaProfile"
                  value={formData.socialMediaProfile}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Social media profile"
                />
              </div>
            ) : (
              formData.socialMediaProfile && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Social Media:</span>
                  <a
                    href={formData.socialMediaProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600"
                  >
                    {formData.socialMediaProfile}
                  </a>
                </div>
              )
            )}

            {isEditing && (
              <div className="mt-2">
                <Button className="bg-blue-500 text-white" onClick={handleSave}>
                  Save
                </Button>
                <Button
                  className="bg-blue-500 text-white mx-2"
                  onClick={()=>{
                    handleCancel()
                    handleEditClick()
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
